import React, { useRef, useState, useMemo, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NewModal as Modal, CompositeTable as Table, Button, Heading, Row, Col } from '@digitools/honest-react';
import SubsAndLocsColumns from '../../createMyReport/Constants/SubsLocsColumns';
import { UnexpectedErrorContext } from 'src/providers/UnexpectedErrorProvider';
import { AccessLevelEnum } from '../types/permissionTypes';
import useTealium from '../../../hooks/useTealium';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import { useDisclosure } from '@mantine/hooks';
const ButtonLabelText = {
    SelectAll: 'Select All Subsidiaries/Locations',
    DeselectAll: 'Deselect All Subsidiaries/Locations',
};
// Map our internal cycle state to the library's aria-sort vocabulary.
const AriaSortDirection = {
    none: 'none',
    asc: 'ascending',
    desc: 'descending',
};
const SortAnnouncement = {
    none: 'List restored to the default order.',
    asc: 'Selected subsidiaries/locations moved to the top of the list.',
    desc: 'Selected subsidiaries/locations moved to the bottom of the list.',
};
const EmployerSubLocModal = (props) => {
    const { trackEvent } = useTealium();
    const trackOpenModel = () => {
        return (trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
            event_type: TEALIUM_EVENT.EVENT_TYPE.EMPLOYER_SUBS_LOCS_MODAL,
            event_name: 'Open Subsidiaries/Locations modal popup',
        }));
    };
    const trackSaveSubLocs = () => {
        return (trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
            event_type: TEALIUM_EVENT.EVENT_TYPE.EMPLOYER_SUBS_LOCS_MODAL,
            event_name: 'Save Subsidiaries/Locations',
        }));
    };
    const trackCancelSubLocs = () => {
        return (trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
            event_type: TEALIUM_EVENT.EVENT_TYPE.EMPLOYER_SUBS_LOCS_MODAL,
            event_name: 'Cancel Subsidiaries/Locations',
        }));
    };
    const trackSelectDeselectSubLoc = () => {
        return (trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
            event_type: TEALIUM_EVENT.EVENT_TYPE.EMPLOYER_SUBS_LOCS_MODAL,
            event_name: 'Select/Deselect All Subsidiaries/Locations',
        }));
    };
    const trackSortSubLocs = () => {
        return (trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
            event_type: TEALIUM_EVENT.EVENT_TYPE.EMPLOYER_SUBS_LOCS_MODAL,
            event_name: 'Sort Subsidiaries/Locations by selected',
        }));
    };
    const tableApi = useRef({ setSelectedData: () => { } });
    const selectedSubLocObj = useRef([]);
    const reportFilterCategory = useState();
    const [buttonLabels, setButtonLabels] = useState();
    const [tableRenderKey, setTableRenderKey] = useState(0);
    const [sortDirection, setSortDirection] = useState('none');
    const [orderedSubLocs, setOrderedSubLocs] = useState(props.subLocs);
    const [sortAnnouncement, setSortAnnouncement] = useState('');
    const originalOrderRef = useRef([]);
    const [selectedLocationCount, setSelectedLocationCount] = useState(0);
    const [selectHeaderEl, setSelectHeaderEl] = useState(null);
    const { setIsMessagingDisabled } = useContext(UnexpectedErrorContext);
    useEffect(() => setIsMessagingDisabled(false), []);
    const getSelectAllButtonLabel = (selectedArrayLength) => {
        setSelectedLocationCount(selectedArrayLength);
        setButtonLabels(selectedArrayLength === props.subLocs.length ? ButtonLabelText.DeselectAll : ButtonLabelText.SelectAll);
    };
    const setTableApi = (api) => {
        tableApi.current = api;
    };
    const getSelectedSubLocObject = (selectedSubLoc) => {
        const selectedIds = selectedSubLoc.current.map(ref => ref.locationId);
        return selectedIds;
    };
    const setSelectedSubLocObject = (selectedSubLoc) => {
        const selectedIds = selectedSubLoc.current.map(ref => ref.locationId);
        props.subLocs.forEach(obj => {
            obj.selected = false;
        });
        selectedIds.forEach(id => {
            props.subLocs
                .filter(obj => obj.locationId === id)
                .forEach(obj => {
                obj.selected = true;
            });
        });
    };
    const getSubAndLocAccessLevel = (selectedSubLoc) => {
        const selectedIds = selectedSubLoc.current.map(ref => ref.locationId);
        if (selectedIds === undefined || selectedIds.length === 0) {
            return AccessLevelEnum.NO_ACCESS;
        }
        else if (props.subLocs.length === selectedIds.length) {
            return AccessLevelEnum.FULL;
        }
        else {
            return AccessLevelEnum.LIMITED;
        }
    };
    const ModifySubLocButton = ({ showModal }) => {
        const handleOnClick = async () => {
            trackOpenModel();
            // Use the returned list, not props.subLocs: on the first open the parent's setSubLocs hasn't
            // committed yet, so props.subLocs would still be empty in this tick.
            const subLocData = await props.getSubsLocData();
            // Work on per-row copies so an in-session sort never mutates the parent's saved
            // data. Selection must only persist on Save (which writes back to props.subLocs),
            // never as a side effect of sorting — see syncSelectionToOrderedData.
            const workingCopy = subLocData.map(subLoc => ({ ...subLoc }));
            originalOrderRef.current = workingCopy;
            setOrderedSubLocs([...workingCopy]);
            setSortDirection('none');
            setSortAnnouncement('');
            getSelectAllButtonLabel(selectedSubLocObj.current.length);
            setTableRenderKey(key => key + 1);
            showModal();
        };
        return (<Button buttonType={'text'} type={'button'} onClick={handleOnClick} data-testid='openModal' aria-label='Open Subsidiaries/Locations modal popup'>
        Modify Subsidiaries/Locations
      </Button>);
    };
    const SaveChangesButton = async () => {
        trackSaveSubLocs();
        setSelectedSubLocObject(selectedSubLocObj);
        const subAndLocAccessLevel = getSubAndLocAccessLevel(selectedSubLocObj);
        const selectedIds = getSelectedSubLocObject(selectedSubLocObj);
        await props.saveSubsAndLocs(selectedIds, subAndLocAccessLevel);
        close();
    };
    const selectDeSelectSubsLoc = () => {
        trackSelectDeselectSubLoc();
        buttonLabels === ButtonLabelText.SelectAll
            ? tableApi.current.setSelectedData(props.subLocs)
            : tableApi.current.setSelectedData([]);
    };
    // Write the live checkbox selection onto the modal's working copies (originalOrderRef)
    // so the table — which re-mounts on sort and derives its initial selection from each
    // row's `selected` flag — restores the user's in-session selection. This deliberately
    // touches only the local copies, NOT props.subLocs, so an unsaved sort never persists.
    const syncSelectionToOrderedData = () => {
        const selectedIds = new Set(selectedSubLocObj.current.map(ref => ref.locationId));
        originalOrderRef.current.forEach(item => {
            item.selected = selectedIds.has(item.locationId);
        });
    };
    // Reorder a copy of the data so currently-selected rows group at the top ('asc') or
    // bottom ('desc'), preserving the original relative order within each group. 'none'
    // restores the snapshot taken when the modal opened. Selection is read live from the
    // selectedRef, so the reorder reflects unsaved checkbox changes.
    const reorderBySelected = (direction) => {
        if (direction === 'none') {
            return [...originalOrderRef.current];
        }
        const selectedIds = new Set(selectedSubLocObj.current.map(ref => ref.locationId));
        const selected = originalOrderRef.current.filter(item => selectedIds.has(item.locationId));
        const unselected = originalOrderRef.current.filter(item => !selectedIds.has(item.locationId));
        return direction === 'asc' ? [...selected, ...unselected] : [...unselected, ...selected];
    };
    const sortBySelected = () => {
        trackSortSubLocs();
        const nextDirection = sortDirection === 'none' ? 'asc' : sortDirection === 'asc' ? 'desc' : 'none';
        // Preserve the in-session selection across the table re-mount WITHOUT touching
        // props.subLocs — sorting must never commit selections (only Save does).
        syncSelectionToOrderedData();
        setOrderedSubLocs(reorderBySelected(nextDirection));
        setSortDirection(nextDirection);
        setSortAnnouncement(SortAnnouncement[nextDirection]);
        setTableRenderKey(key => key + 1);
    };
    const table = useMemo(() => (<Table key={tableRenderKey} title={'subsidiary and location table'} data={orderedSubLocs} defaultPageSize={10} columns={SubsAndLocsColumns} paginated={true} filterable={true} isSelectable={true} selectedRef={selectedSubLocObj} rowIdentifier={'locationId'} data-testid={'subLocTable'} tableApi={setTableApi} onSelectedChange={(enabled) => getSelectAllButtonLabel(enabled.length)}/>), [tableRenderKey, orderedSubLocs, sortDirection]);
    // A callback ref (not an effect) so this runs synchronously in the same commit that mounts
    // or remounts the table — an effect fires a tick later, which races against the Modal's own
    // mount timing. Keying the wrapper to tableRenderKey forces it to remount alongside the
    // table, so the callback re-fires and re-locates the (new) Select header <th> after every sort.
    const locateSelectHeader = (node) => {
        if (!node) {
            setSelectHeaderEl(null);
            return;
        }
        const ths = node.querySelectorAll('thead th');
        const selectTh = Array.from(ths).find(th => /Select/.test(th.textContent || ''));
        setSelectHeaderEl(selectTh ?? null);
    };
    // aria-sort must live on the library's own <th>, not on our portaled button, so it's set
    // imperatively here rather than through JSX.
    useEffect(() => {
        selectHeaderEl?.setAttribute('aria-sort', AriaSortDirection[sortDirection]);
    }, [selectHeaderEl, sortDirection]);
    const [isOpen, { open, close }] = useDisclosure(false);
    return (<>
      <Modal title={'Modify Subsidiaries/Locations'} centered={true} opened={isOpen} onClose={() => {
            trackCancelSubLocs();
            close();
        }} cancelButton={false} submitButton={false} closeOnClickOutside={true} size={'1100px'}>
        <Row style={{ alignItems: 'center' }}>
          <Col>
            <Heading style={{ marginBottom: '10px' }} data-testid={'selectedSubLocHeader'} elemType={'h3'} elemStyle={'h6'} aria-label='Subsidiaries/Locations Selected'>
              {selectedLocationCount} of {props.subLocs.length} subsidiaries/locations selected
            </Heading>
          </Col>
          <Col>
            <Button style={{ marginBottom: '15px', float: 'right' }} buttonType={'secondary'} data-testid={'selectSubs'} type={'button'} onClick={selectDeSelectSubsLoc} aria-label='Select/Deselect All Subsidiaries/Locations'>
              {buttonLabels}
            </Button>
          </Col>
        </Row>
        {/* US-007: the sort trigger is portaled into the Select column header's <th> below.
            This visually-hidden live region announces the reorder result to screen readers. */}
        <span data-testid={'sortSelectedSubLocsStatus'} role={'status'} className={'sr-only'}>
          {sortAnnouncement}
        </span>
        <div key={tableRenderKey} ref={locateSelectHeader}>
          {table}
          {selectHeaderEl &&
            createPortal(<button type='button' onClick={sortBySelected} aria-label='Sort by selected subsidiaries/locations' style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: '0.25rem' }}>
                {sortDirection === 'asc' ? '▲' : sortDirection === 'desc' ? '▼' : '⇅'}
              </button>, selectHeaderEl)}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '0.5rem', marginTop: '1rem' }}>
          <Button buttonType={'primary'} type={'button'} data-testid={'saveSubLocs'} aria-label='Save Subsidiaries/Locations' onClick={() => SaveChangesButton()}>
            Save Changes
          </Button>
          <Button buttonType={'secondary'} type={'button'} data-testid={'cancelSubLocs'} aria-label='Cancel Subsidiaries/Locations' onClick={() => {
            trackCancelSubLocs();
            close();
        }}>
            Cancel
          </Button>
        </div>
      </Modal>
      <ModifySubLocButton filterCategory={reportFilterCategory} showModal={() => open()}/>
    </>);
};
export default EmployerSubLocModal;
